import type { ReactNode } from 'react';
import clsx from 'clsx';

/**
 * Shared page-level layout primitives (see the desktop layout spec). These
 * unify spacing/width rules across every page instead of each page hand-
 * rolling its own root `flex flex-col gap-4 p-4` wrapper, title row and
 * two-column split.
 */

export function Page({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div data-page className={clsx('page-stack', className)}>
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div data-page-header className="flex flex-wrap items-center justify-between gap-3 min-h-12">
      <div className="min-w-0">
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="field-help">{subtitle}</p>}
      </div>
      {actions && <div className="btn-row">{actions}</div>}
    </div>
  );
}

export function Section({
  title,
  action,
  children,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="section-stack">
      <div className="flex items-center justify-between gap-3">
        <h2 className="section-title">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Field({
  label,
  htmlFor,
  help,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  help?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor} className="field-label">
        {label}
      </label>
      {children}
      {help && <p className="field-help">{help}</p>}
    </div>
  );
}

export function Split({
  aside,
  mobileAside = 'last',
  children,
}: {
  aside: ReactNode;
  mobileAside?: 'first' | 'last';
  children: ReactNode;
}) {
  // top-24 (6rem) was header(4rem) + a 2rem gap; kept as
  // `--app-header-h + 2rem` so this still sits 2rem below the header at any
  // height, including training's taller one - see theme/tokens.css.
  const a = (
    <aside className="flex flex-col gap-4 lg:gap-6 lg:order-2 lg:sticky lg:top-[calc(var(--app-header-h)_+_2rem)]">
      {aside}
    </aside>
  );
  const m = <div className="flex flex-col gap-4 lg:gap-6 min-w-0 lg:order-1">{children}</div>;
  return (
    <div className="split">
      {mobileAside === 'first' ? (
        <>
          {a}
          {m}
        </>
      ) : (
        <>
          {m}
          {a}
        </>
      )}
    </div>
  );
}
